import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertButton, AlertController } from '@ionic/angular';
import {
  MonoTypeOperatorFunction,
  Subject,
  TimeoutError,
  pipe,
  retry,
  switchMap,
  throwError,
  timeout
} from 'rxjs';
import { Store } from '@ngxs/store';
import _ from 'lodash';

import { CustomErrorCode, ErrorConstant } from 'src/app/constants';
import { ApiType, PosConfig } from 'src/app/pos.config';
import { PosLoadingModalService } from 'src/app/components/shared/loading-modal/service';
import { MenuConstant } from 'src/app/constants/menu.constant';
import { APP_STATE_TOKEN } from 'src/app/store/app/app.state.model';
import { TranslocoService } from '@ngneat/transloco';
import { ResetApplication } from 'src/app/store/app/app.action';

type RetryPipe<T = any> = MonoTypeOperatorFunction<T>;

enum NoRetryErrorEnum {
  Unauthorized,
  NoNetwork,
  NotFound,
  ApplicationError
}

@Injectable({ providedIn: 'root' })
export class ErrorService {
  private retryAlertNotifiers: Subject<string>[] = [];
  private isRetryAlertPresent = false;
  private isAlertErrorPresent = false;

  constructor(
    private readonly alertCtrl: AlertController,
    private readonly posLoadingModal: PosLoadingModalService,
    private readonly store: Store,
    private readonly translocoService: TranslocoService
  ) {}

  alertError(message: string) {
    this.alertCtrl
      .create({
        header: this.translocoService.translate('common.error'),
        message,
        backdropDismiss: false,
        cssClass: 'alert-message',
        buttons: [
          {
            text: this.translocoService.translate('common.ok'),
            role: 'ok'
          }
        ]
      })
      .then((alert) => {
        alert.present();
        alert.onWillDismiss().then(() => {
          this.isAlertErrorPresent = false;
        });
      });
  }

  retryPipe = <T>(type?: ApiType): RetryPipe<T> => {
    const retryFunc = retry({ delay: (error) => this.retry(error) });
    const timeoutFunc = () =>
      type ? timeout(PosConfig.POS_API_TIMEOUT_TIME(type)) : pipe();

    switch (type) {
      case ApiType.MenuTallyLoading:
        const menuTallyLoadingRetryFunc = retry({
          delay: (error) => {
            this.posLoadingModal.dismiss();
            return this.retry(error).pipe(
              switchMap(() =>
                this.posLoadingModal.presentLoadingModal(
                  this.translocoService.translate(
                    MenuConstant.LOADING_MODAL_MSG.MENU_TALLY_LOADING
                  ),
                  this.translocoService.translate(
                    MenuConstant.LOADING_MODAL_MSG.MENU_TALLY_LOADING_LONGER
                  )
                )
              )
            );
          }
        });

        return pipe(timeoutFunc(), menuTallyLoadingRetryFunc) as RetryPipe;
      case ApiType.POSItemsManagingLoading:
        const posItemsLoadingRetryFunc = retry({
          delay: (error) => {
            this.posLoadingModal.dismiss();
            return this.retry(error).pipe(
              switchMap(() =>
                this.posLoadingModal.presentLoadingModal(
                  this.translocoService.translate(
                    MenuConstant.LOADING_MODAL_MSG.LOADING_ITEMS
                  ),
                  this.translocoService.translate(
                    MenuConstant.LOADING_MODAL_MSG.MENU_TALLY_LOADING_LONGER
                  )
                )
              )
            );
          }
        });

        return pipe(timeoutFunc(), posItemsLoadingRetryFunc) as RetryPipe;
      case ApiType.Mutate:
      case ApiType.Query:
        return pipe(timeoutFunc(), retryFunc) as RetryPipe;
      default:
        return pipe(retryFunc) as RetryPipe;
    }
  };

  private getNoRetryErrorType(
    httpStatus?: number,
    bodyStatus?: string
  ): NoRetryErrorEnum | null {
    if (!this.store.selectSnapshot(APP_STATE_TOKEN).networkConnected) {
      return NoRetryErrorEnum.NoNetwork;
    }

    switch (httpStatus) {
      case HttpStatusCode.Unauthorized:
        return NoRetryErrorEnum.Unauthorized;
      case HttpStatusCode.NotFound:
        return NoRetryErrorEnum.NotFound;
      default:
        break;
    }

    if (
      bodyStatus &&
      ErrorConstant.APPLICATION_ERROR_STATUSES.includes(bodyStatus)
    ) {
      return NoRetryErrorEnum.ApplicationError;
    }

    return null;
  }

  private readonly retry = (
    error: HttpErrorResponse | TimeoutError | Error
  ) => {
    const httpStatus = 'status' in error ? error.status : undefined;
    const bodyStatus = 'error' in error ? error.error?.status : undefined;
    const noRetry = this.getNoRetryErrorType(httpStatus, bodyStatus);

    let alertMessage: string | undefined;

    if (!_.isNil(noRetry)) {
      switch (noRetry) {
        case NoRetryErrorEnum.Unauthorized:
          alertMessage =
            'The session has expired or the device was unregistered.';
          this.store.dispatch(new ResetApplication());
          break;
        case NoRetryErrorEnum.NoNetwork:
          alertMessage = this.getErrorMessageByCode(
            CustomErrorCode.DeviceOffline
          );
          break;
        case NoRetryErrorEnum.ApplicationError:
          alertMessage =
            'error' in error && error.error?.message
              ? error.error.message
              : this.getErrorMessageByCode(CustomErrorCode.UnknownError);
          break;
        default:
          break;
      }

      if (alertMessage && !this.isAlertErrorPresent) {
        this.isAlertErrorPresent = true;
        this.alertError(alertMessage);
      }

      return throwError(() => error);
    }

    const retryNotifier = new Subject<string>();

    this.retryAlertNotifiers.push(retryNotifier);

    this.alertCtrl.getTop().then(() => {
      if (!this.isRetryAlertPresent) {
        const isTimeoutError = error instanceof TimeoutError;

        this.isRetryAlertPresent = true;
        this.retryAlert(
          this.getErrorMessageByParam(isTimeoutError, httpStatus, error)
        ).then((isRetry) => {
          if (isRetry) {
            this.retryAlertNotifiers.forEach((notifier) => notifier.next(''));
          } else {
            this.retryAlertNotifiers.forEach((notifier) =>
              notifier.error(error)
            );
          }

          this.retryAlertNotifiers = [];
          this.isRetryAlertPresent = false;
        });
      }
    });

    return retryNotifier.asObservable();
  };

  private getErrorMessageByCode(code: string | number): string {
    const translationKey =
      ErrorConstant.ErrorMessageByCode[code] || 'errors.naError';
    return this.translocoService.translate(translationKey);
  }

  private getErrorMessageByParam(
    isTimeoutError: boolean,
    httpStatus: number | undefined,
    error?: HttpErrorResponse | TimeoutError | Error
  ): string {
    let errorMessage =
      error && 'error' in error ? error.error.message : undefined;
    if (
      error &&
      'error' in error &&
      error.error.status === ErrorConstant.TICKET_ITEM_REFUND_IN_PROGRESS_STATUS
    ) {
      errorMessage = this.translocoService.translate(
        'errors.refundInProgress',
        { uuid: error.error.message }
      );
    }
    return errorMessage
      ? errorMessage
      : isTimeoutError
      ? this.getErrorMessageByCode(CustomErrorCode.RequestTimeout)
      : httpStatus === 0
      ? this.getErrorMessageByCode(CustomErrorCode.HttpNoResponseError)
      : this.getErrorMessageByCode(HttpStatusCode.InternalServerError);
  }

  private async retryAlert(message: string): Promise<boolean> {
    const buttons: (string | AlertButton)[] = [
      {
        text: this.translocoService.translate('common.cancel'),
        cssClass: 'alert-button-cancel'
      },
      {
        text: this.translocoService.translate('common.retry'),
        role: 'retry',
        cssClass: 'alert-button-retry'
      }
    ];
    const alert = await this.alertCtrl.create({
      cssClass: 'custom-alert-error-1',
      header: this.translocoService.translate('common.error'),
      mode: 'md',
      message,
      buttons
    });
    await alert.present();
    const result = await alert.onDidDismiss();
    return result.role === 'retry';
  }
}
